import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, trackClick } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { CheckCircle2, Instagram, Bot, Send, MessageCircle, Mail } from "lucide-react";

const REASONS = [
  { value: "assistenza_registrazione", label: "Assistenza registrazione" },
  { value: "info_promozione", label: "Informazioni su una promozione" },
  { value: "problema_link", label: "Problema con un link" },
  { value: "collaborazione", label: "Collaborazione" },
  { value: "candidatura_attivita", label: "Candidatura attività commerciale" },
  { value: "candidatura_collaboratore", label: "Candidatura collaboratore" },
  { value: "altro", label: "Altro" },
];

const INITIAL = { name: "", email: "", phone: "", reason: "assistenza_registrazione", bookmaker: "", message: "", privacy_consent: false };

export default function Contatti() {
  const s = useSettings();
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_consent) { toast.error("Devi accettare l'informativa privacy."); return; }
    setSending(true);
    try {
      await api.post("/contacts", form);
      setDone(true); setForm(INITIAL);
      toast.success("Messaggio inviato. Ti contatteremo al più presto.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSending(false); }
  };

  const input = "w-full rounded-lg bg-black/40 border border-white/10 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/30 focus:outline-none px-3.5 py-2.5 text-sm text-white placeholder:text-zinc-500";

  const CARDS = [
    { icon: Bot, label: "Telegram Bot", link: s.telegram_bot_url, kind: "telegram_bot", value: "@V_vinko_bot" },
    { icon: Send, label: "Telegram gratuito", link: s.telegram_free_url, kind: "telegram_free", value: "Canale gratuito" },
    { icon: Instagram, label: "Instagram", link: s.instagram_url, kind: "instagram", value: "@v_vinko_" },
    { icon: MessageCircle, label: "WhatsApp", link: s.whatsapp_url, kind: "whatsapp", value: "Chat diretta" },
    { icon: Mail, label: "Email", link: `mailto:${s.vinko_email}`, kind: null, value: s.vinko_email },
  ];

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-14 sm:py-20">
      <span className="text-xs font-bold uppercase tracking-[0.25em] text-yellow-500">Assistenza</span>
      <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter">
        Contatta <span className="text-yellow-400">Vinko</span>
      </h1>
      <p className="mt-4 max-w-2xl text-zinc-400">
        Hai bisogno di assistenza o vuoi maggiori informazioni? Contatta Vinko
        attraverso uno dei nostri canali ufficiali.
      </p>

      <div className="mt-10 grid gap-4 md:grid-cols-3 lg:grid-cols-5">
        {CARDS.map(({ icon: Ic, label, link, kind, value }) => (
          <a
            key={label}
            href={link}
            target={kind ? "_blank" : undefined}
            rel="noopener"
            onClick={() => kind && trackClick(kind)}
            data-testid={`contact-card-${label.toLowerCase().replace(/\s/g, "-")}`}
            className="rounded-2xl border border-white/10 bg-[#101010] p-5 hover:border-yellow-400/40 transition-colors duration-200"
          >
            <Ic className="w-5 h-5 text-yellow-400 mb-3" />
            <div className="font-semibold text-sm">{label}</div>
            <div className="text-xs text-zinc-400 mt-1 break-all">{value}</div>
          </a>
        ))}
      </div>

      <div className="mt-14 rounded-2xl border border-white/10 bg-[#0F0F0F] p-6 sm:p-8">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Scrivici un messaggio</h2>
        {done ? (
          <div className="mt-6 rounded-xl border border-yellow-400/40 bg-yellow-400/5 p-6 flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-yellow-400 shrink-0" />
            <div>
              <div className="font-semibold text-white">Messaggio inviato correttamente.</div>
              <p className="text-sm text-zinc-300 mt-1">Ti risponderemo il prima possibile.</p>
              <button onClick={() => setDone(false)} className="mt-4 text-sm text-yellow-400 hover:text-yellow-300 font-semibold">Invia un altro messaggio →</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="contact-form" className="mt-6 grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Nome *</label>
              <input required className={input} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Email *</label>
              <input required type="email" className={input} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Telefono</label>
              <input className={input} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Motivo *</label>
              <select className={input} value={form.reason} onChange={(e) => set("reason", e.target.value)} data-testid="contact-reason">
                {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest text-zinc-500">Bookmaker interessato</label>
              <input className={input} value={form.bookmaker} onChange={(e) => set("bookmaker", e.target.value)} placeholder="Es. Betpassion" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest text-zinc-500">Messaggio *</label>
              <textarea required rows={5} className={input} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message" />
            </div>
            <label className="md:col-span-2 flex items-start gap-3 text-sm text-zinc-300">
              <input type="checkbox" checked={form.privacy_consent} onChange={(e) => set("privacy_consent", e.target.checked)} className="mt-1 h-4 w-4 accent-yellow-400" required data-testid="contact-privacy" />
              <span>Ho letto e accetto l'<a href="/privacy" className="text-yellow-400 underline">informativa privacy</a>.</span>
            </label>
            <div className="md:col-span-2">
              <button type="submit" disabled={sending} data-testid="contact-submit" className="w-full sm:w-auto rounded-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-60 text-black font-bold text-sm px-6 py-3 transition-transform duration-200 hover:scale-[1.02]">
                {sending ? "Invio in corso..." : "INVIA MESSAGGIO"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
